import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { groupInvitationsByProject } from "@/app/invitations/lib/utils";
import { onboardingKeys } from "@/app/onboarding/api/query-keys";
import { orgProjectKeys } from "@/lib/org-project-context/coordinator-query";
import { useScopeKey } from "@/lib/org-project-context/hooks";
import { useResourceScope } from "@/lib/resource-context";
import { acceptInvitation } from "./accept-invitation";
import { createProjectInvitation } from "./create-project-invitation";
import { deleteProjectInvitation } from "./delete-project-invitation";
import { myInvitationsQueryOptions } from "./get-my-invitations";
import { projectInvitationsQueryOptions } from "./get-project-invitations";
import { invitationKeys } from "./query-keys";
import { rejectInvitation } from "./reject-invitation";

export const useGetProjectInvitations = () => {
  const scope = useResourceScope();

  return useSuspenseQuery({
    ...projectInvitationsQueryOptions(scope.organizationId, scope.projectId),
  });
};

export const useCreateProjectInvitation = () => {
  const scope = useResourceScope();

  return useMutation({
    mutationFn: ({ email, role }: { email: string; role?: string }) =>
      createProjectInvitation({ email, role, scope }),
    meta: {
      invalidates: [invitationKeys.lists({ scope })],
    },
    onSuccess: () => {
      toast.success("Invitation sent successfully");
    },
    onError: () => {
      toast.error("Failed to send invitation");
    },
  });
};

export const useDeleteProjectInvitation = () => {
  const scope = useResourceScope();

  return useMutation({
    mutationFn: ({ invitationId }: { invitationId: string }) =>
      deleteProjectInvitation({ invitationId, scope }),
    meta: {
      invalidates: [invitationKeys.lists({ scope })],
    },
    onSuccess: () => {
      toast.success("Invitation deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete invitation");
    },
  });
};

export const useGetMyInvitations = () =>
  useSuspenseQuery({
    ...myInvitationsQueryOptions,
  });

export const useGetGroupedInvitations = () =>
  useSuspenseQuery({
    ...myInvitationsQueryOptions,
    select: (data) => {
      const invitations = groupInvitationsByProject(data.items);
      return {
        invitations,
        invitationCount: invitations.length,
      };
    },
  });

export const useGetGroupedInvitationsByProject = (
  projectId: string | undefined
) =>
  useSuspenseQuery({
    ...myInvitationsQueryOptions,
    select: (data) => {
      const invitations = groupInvitationsByProject(data.items);
      const filtered = projectId
        ? invitations.filter((inv) => inv.project_id === projectId)
        : invitations;
      return {
        invitations: filtered,
        invitationCount: filtered.length,
      };
    },
  });

export const useAcceptInvitation = () => {
  const scopeKey = useScopeKey();

  return useMutation({
    mutationFn: acceptInvitation,
    meta: {
      // Membership changes are project-scoped; eligibility has no invitation id
      // here so invalidate every cached eligibility result. Accepting also
      // changes org membership, so refresh the org-project context queries that
      // back the Orgs dropdown (mirrors useAcceptAndSwitch).
      invalidates: [
        invitationKeys.myInvitations(),
        onboardingKeys.all(),
        orgProjectKeys.all(scopeKey),
      ],
    },
    onSuccess: () => {
      toast.success("Invitation accepted successfully");
    },
    onError: () => {
      toast.error("Failed to accept invitation");
    },
  });
};

export const useRejectInvitation = () => {
  return useMutation({
    mutationFn: rejectInvitation,
    meta: {
      invalidates: [invitationKeys.myInvitations()],
    },
    onSuccess: () => {
      toast.success("Invitation rejected successfully");
    },
    onError: () => {
      toast.error("Failed to reject invitation");
    },
  });
};
